import type { ChatMessageProps } from "@albertine/design-system/components/chat";

export type ConciergeLinkContext = {
    parentOrigin: string | null;
    itemLinkPrefix: string | null;
    requestId?: string;
};

export function articleHref(
    context: ConciergeLinkContext,
    articleId: string,
): string | undefined {
    return itemPath(context, "article", articleId);
}

export function proposalTitleHref(
    context: ConciergeLinkContext,
    requestId: string,
): string | undefined {
    return itemPath(context, "proposal", requestId);
}

export function proposalBookingHref(args: {
    context: ConciergeLinkContext;
    requestId: string;
    bookingId: string;
    index: number;
    bookingsLength: number;
    proposalBookingCount?: number;
}): string | undefined {
    const scrollToId = proposalScrollToId({
        index: args.index,
        bookingsLength: args.bookingsLength,
        proposalBookingCount: args.proposalBookingCount,
        bookingId: args.bookingId,
    });

    const base = itemPath(args.context, "proposal", args.requestId);
    if (!base) {
        return undefined;
    }

    return scrollToId ? `${base}#${scrollToId}` : base;
}

export function bookingDetailsHref(
    context: ConciergeLinkContext,
    bookingId: string,
): string | undefined {
    return itemPath(context, "booking", bookingId);
}

export function confirmedBookingHref(args: {
    context: ConciergeLinkContext;
    bookingId: string;
    index: number;
    bookingsLength: number;
    sender: ChatMessageProps["sender"];
    messageRequestId?: string;
}): string | undefined {
    const { context, bookingId, index, bookingsLength, sender, messageRequestId } =
        args;

    if (sender === "member" && messageRequestId) {
        return proposalBookingHref({
            context,
            requestId: messageRequestId,
            bookingId,
            index,
            bookingsLength,
        });
    }

    return bookingDetailsHref(context, bookingId);
}

export function topicHref(
    context: ConciergeLinkContext,
    requestId: string,
): string | undefined {
    if (!context.itemLinkPrefix) {
        return undefined;
    }

    return absoluteConciergeUrl(
        context,
        conversationPathForRequest(context.itemLinkPrefix, requestId),
    );
}

export function proposalScrollToId(args: {
    index: number;
    bookingsLength: number;
    proposalBookingCount?: number;
    bookingId: string;
}): string | undefined {
    const firstOfManySuggestions = args.index === 0 && args.bookingsLength > 1;
    const firstAndSingleSuggestion = args.proposalBookingCount === 1;

    if (firstOfManySuggestions || firstAndSingleSuggestion) {
        return undefined;
    }

    return args.bookingId;
}

export function enrichChatMessageWithConciergeLinks(
    message: ChatMessageProps,
    context: ConciergeLinkContext,
): ChatMessageProps {
    if (!context.parentOrigin || !context.itemLinkPrefix) {
        return message;
    }

    const messageRequestId = context.requestId;
    const proposalBookingCount = message.proposal?.bookings.length;

    return {
        ...message,
        articles: message.articles?.map((article) => ({
            ...article,
            href: articleHref(context, article.id),
        })),
        proposal:
            message.proposal && messageRequestId
                ? {
                      ...message.proposal,
                      href: proposalTitleHref(context, messageRequestId),
                      bookings: message.proposal.bookings.map(
                          (booking, index, bookings) => ({
                              ...booking,
                              href: proposalBookingHref({
                                  context,
                                  requestId: messageRequestId,
                                  bookingId: booking.id,
                                  index,
                                  bookingsLength: bookings.length,
                                  proposalBookingCount,
                              }),
                          }),
                      ),
                  }
                : message.proposal,
        confirmedBookings: message.confirmedBookings?.map(
            (booking, index, bookings) => ({
                ...booking,
                href: confirmedBookingHref({
                    context,
                    bookingId: booking.id,
                    index,
                    bookingsLength: bookings.length,
                    sender: message.sender,
                    messageRequestId,
                }),
            }),
        ),
        topicHref:
            message.topic && messageRequestId
                ? topicHref(context, messageRequestId)
                : undefined,
    };
}

function itemPath(
    context: ConciergeLinkContext,
    kind: "article" | "proposal" | "booking",
    id: string,
): string | undefined {
    if (!context.itemLinkPrefix) {
        return undefined;
    }

    return absoluteConciergeUrl(
        context,
        `${context.itemLinkPrefix}/${kind}/${encodeURIComponent(id)}`,
    );
}

function conversationPathForRequest(itemLinkPrefix: string, requestId: string): string {
    const conversationsRoot = itemLinkPrefix.slice(0, itemLinkPrefix.lastIndexOf("/"));
    return `${conversationsRoot}/${encodeURIComponent(requestId)}`;
}

function absoluteConciergeUrl(
    context: ConciergeLinkContext,
    path: string,
): string | undefined {
    if (!context.parentOrigin) {
        return undefined;
    }

    return new URL(path, context.parentOrigin).toString();
}
